/**
 * Modal Configuration Utilities
 * Provides configuration objects for UniversalSelectModal based on display format types
 */

/**
 * Get modal configuration for UniversalSelectModal
 * @param {string} format - Format type: 'nameCode', 'codeName', 'codeDesc', 'isoDesc', 'nameOnly'
 * @param {boolean} isSpanish - Language flag for sort options
 * @returns {Object} Configuration object for UniversalSelectModal
 */
export function getModalConfig(format, isSpanish = false) {
  const baseSortOptions = [
    { value: 'name-asc', label: isSpanish ? 'Nombre (A-Z)' : 'Name (A-Z)' },
    { value: 'name-desc', label: isSpanish ? 'Nombre (Z-A)' : 'Name (Z-A)' },
    { value: 'code-asc', label: isSpanish ? 'Código (A-Z)' : 'Code (A-Z)' },
    { value: 'code-desc', label: isSpanish ? 'Código (Z-A)' : 'Code (Z-A)' },
  ];

  const baseSortFields = {
    'name-asc': 'nombre',
    'name-desc': 'nombre',
    'code-asc': 'codigo',
    'code-desc': 'codigo',
  };

  switch (format) {
    case 'nameCode': // Name (primary) - Code (secondary)
      return {
        fields: {
          primary: 'nombre',
          secondary: 'codigo',
          searchFields: ['nombre', 'codigo'],
          sortFields: baseSortFields,
        },
        displayFormat: 'name-code',
        sortOptions: baseSortOptions,
        defaultSort: 'name-asc',
      };

    case 'codeName': // Code (primary) - Name (secondary)
      return {
        fields: {
          primary: 'codigo',
          secondary: 'nombre',
          searchFields: ['codigo', 'nombre'],
          sortFields: baseSortFields,
        },
        displayFormat: 'code-name',
        sortOptions: baseSortOptions,
        defaultSort: 'code-asc',
      };

    case 'codeDesc': // Code (primary) - Description (secondary)
      return {
        fields: {
          primary: 'codigo',
          secondary: 'descripcion',
          searchFields: ['codigo', 'descripcion'],
          sortFields: {
            ...baseSortFields,
            'desc-asc': 'descripcion',
            'desc-desc': 'descripcion',
          },
        },
        displayFormat: 'code-name',
        sortOptions: [
          { value: 'code-asc', label: isSpanish ? 'Código (A-Z)' : 'Code (A-Z)' },
          { value: 'code-desc', label: isSpanish ? 'Código (Z-A)' : 'Code (Z-A)' },
          { value: 'desc-asc', label: isSpanish ? 'Descripción (A-Z)' : 'Description (A-Z)' },
          { value: 'desc-desc', label: isSpanish ? 'Descripción (Z-A)' : 'Description (Z-A)' },
        ],
        defaultSort: 'code-asc',
      };

    case 'isoDesc': // ISO (primary) - Description (secondary)
      return {
        fields: {
          primary: 'iso',
          secondary: 'descripcion',
          searchFields: ['iso', 'descripcion', 'codigo'],
          sortFields: {
            'iso-asc': 'iso',
            'iso-desc': 'iso',
            'desc-asc': 'descripcion',
            'desc-desc': 'descripcion',
            'code-asc': 'codigo',
            'code-desc': 'codigo',
          },
        },
        displayFormat: 'primary-secondary', // Use primary-secondary to show iso (not codigo)
        sortOptions: [
          { value: 'iso-asc', label: isSpanish ? 'ISO (A-Z)' : 'ISO (A-Z)' },
          { value: 'iso-desc', label: isSpanish ? 'ISO (Z-A)' : 'ISO (Z-A)' },
          { value: 'desc-asc', label: isSpanish ? 'Descripción (A-Z)' : 'Description (A-Z)' },
          { value: 'desc-desc', label: isSpanish ? 'Descripción (Z-A)' : 'Description (Z-A)' },
          { value: 'code-asc', label: isSpanish ? 'Código (A-Z)' : 'Code (A-Z)' },
          { value: 'code-desc', label: isSpanish ? 'Código (Z-A)' : 'Code (Z-A)' },
        ],
        defaultSort: 'iso-asc',
      };

    case 'nameOnly': // Name only (no secondary field)
      return {
        fields: {
          primary: 'nombre',
          secondary: null,
          searchFields: ['nombre', 'codigo'],
          sortFields: {
            'name-asc': 'nombre',
            'name-desc': 'nombre',
          },
        },
        displayFormat: 'primary-secondary',
        sortOptions: [
          { value: 'name-asc', label: isSpanish ? 'Nombre (A-Z)' : 'Name (A-Z)' },
          { value: 'name-desc', label: isSpanish ? 'Nombre (Z-A)' : 'Name (Z-A)' },
        ],
        defaultSort: 'name-asc',
      };

    default:
      // Default to nameCode format
      return {
        fields: {
          primary: 'nombre',
          secondary: 'codigo',
          searchFields: ['nombre', 'codigo'],
          sortFields: baseSortFields,
        },
        displayFormat: 'name-code',
        sortOptions: baseSortOptions,
        defaultSort: 'name-asc',
      };
  }
}

